import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SERVICES = [
  "Partial Service Catering",
  "Full Service Catering",
  "Private Chef Experiences",
  "Corporate Catering",
  "General Inquiry",
];

export default function Contact() {
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifyLoading(true);
    try {
      const { data } = await api.post("/notify", { email: notifyEmail });
      toast.success(data.message || "You're on the list.");
      setNotifyEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Your inquiry has been sent.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-[#F5F2EB]/10 rounded-sm px-4 py-3.5 text-[#F5F2EB] placeholder:text-[#F5F2EB]/30 text-sm focus:outline-none focus:border-[#B5643C] transition-colors duration-300";

  return (
    <section data-testid="contact-section" className="relative py-28 md:py-40 px-6 border-t border-[#B5643C]/20">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
        {/* Left: notify + socials */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[11px] tracking-[0.5em] uppercase text-[#B5643C] mb-6">Be first to know</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#F5F2EB] leading-[1.05] mb-8">
            Get notified <br /> when we launch.
          </h2>

          <form onSubmit={submitNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mb-14">
            <input
              type="email"
              required
              data-testid="notify-email-input"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="your@email.com"
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="notify-submit-button"
              disabled={notifyLoading}
              className="cursor-hover shrink-0 inline-flex items-center justify-center gap-2 bg-[#B5643C] text-[#050505] font-medium text-sm px-6 py-3.5 rounded-sm hover:bg-[#c9744a] transition-colors duration-300 disabled:opacity-60"
            >
              {notifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify me"}
            </button>
          </form>

          <p className="text-[11px] tracking-[0.5em] uppercase text-[#F5F2EB]/40 mb-5">Follow along</p>
          <div className="flex flex-col gap-4">
            <a
              href="https://instagram.com/theensokitchen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-link"
              className="cursor-hover group inline-flex items-center gap-4 text-[#F5F2EB]/70 hover:text-[#B5643C] transition-colors duration-300 w-fit"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-lg">@theensokitchen</span>
              <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
            </a>
            <a
              href="https://facebook.com/theensokitchen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-link"
              className="cursor-hover group inline-flex items-center gap-4 text-[#F5F2EB]/70 hover:text-[#B5643C] transition-colors duration-300 w-fit"
            >
              <Facebook className="w-5 h-5" />
              <span className="text-lg">@theensokitchen</span>
              <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
            </a>
            <a
              href="mailto:theensokitchen@gmail.com"
              data-testid="email-link"
              className="cursor-hover group inline-flex items-center gap-4 text-[#F5F2EB]/70 hover:text-[#B5643C] transition-colors duration-300 w-fit mt-2"
            >
              <span className="text-lg lowercase">theensokitchen@gmail.com</span>
              <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
            </a>
          </div>
        </motion.div>

        {/* Right: inquiry form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[11px] tracking-[0.5em] uppercase text-[#B5643C] mb-6">Enquiries</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#F5F2EB] leading-[1.05] mb-8">
            Start a <span className="italic text-[#B5643C]">conversation.</span>
          </h2>

          <form onSubmit={submitContact} className="flex flex-col gap-4">
            <input
              data-testid="contact-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className={inputCls}
            />
            <input
              type="email"
              data-testid="contact-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Your email"
              className={inputCls}
            />
            <select
              data-testid="contact-service-select"
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              className={`${inputCls} appearance-none`}
            >
              <option value="" className="bg-[#0a0a0a]">Select a service…</option>
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-[#0a0a0a]">
                  {s}
                </option>
              ))}
            </select>
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your event…"
              rows={4}
              className={`${inputCls} resize-none`}
            />
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="cursor-hover inline-flex items-center justify-center gap-2 bg-[#F5F2EB] text-[#050505] font-medium text-sm px-6 py-4 rounded-sm hover:bg-[#B5643C] transition-colors duration-300 disabled:opacity-60 mt-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send inquiry"}
            </button>
          </form>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto mt-16 pt-6 border-t border-[#F5F2EB]/10 flex flex-col sm:flex-row justify-between items-center gap-2">
        <p className="font-serif-display text-xl text-[#F5F2EB]/80 tracking-wide">ENSŌ KITCHEN</p>
        <p className="text-[11px] text-[#F5F2EB]/40 tracking-wider">
          © {new Date().getFullYear()} Enso Kitchen
        </p>
      </div>
    </section>
  );
}
